import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", rate: "" };

export default function PurchaseOrders() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [poRes, vendorRes, rfqRes, prRes, projRes] = await Promise.all([
        api.get("/procurement/purchase-orders"), api.get("/procurement/vendors"),
        api.get("/procurement/rfqs").catch(() => ({ data: [] })), api.get("/procurement/requisitions").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setPos(poRes.data);
      setVendors(vendorRes.data);
      setRfqs(rfqRes.data);
      setRequisitions(prRes.data);
      setProjects(projRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  const act = async (po, action) => {
    setBusyId(po.id);
    try {
      await api.post(`/procurement/purchase-orders/${po.id}/${action}`, action === "submit" ? undefined : {});
      toast.success(`PO ${action}ed`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const linkProject = async (po, projectId) => {
    try {
      await api.patch(`/procurement/purchase-orders/${po.id}/project`, { project_id: projectId });
      toast.success(projectId ? "PO linked to project — its material cost now counts there" : "PO unlinked from project");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <PageHeader title="Purchase Orders" description="Numbered via Pack 1's numbering series and routed through the approval workflow (if configured)." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreatePODialog companies={companies} vendors={vendors} rfqs={rfqs} requisitions={requisitions} projects={projects} onCreated={load} />}</div>} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && pos.length === 0 ? (
          <EmptyState title="No purchase orders yet" testId="purchase-orders-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Project</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(pos, (r) => r.project_id).map((po) => (
                <TableRow key={po.id} data-testid={`po-row-${po.number}`}>
                  <TableCell className="text-xs">
                    <div className="font-mono text-foreground">{po.number}</div>
                    {po.rfq_id && <div className="text-[11px] text-sky-600 dark:text-sky-400">From RFQ {rfqs.find((r) => r.id === po.rfq_id)?.number || "—"}</div>}
                    <ul className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                      {(po.items || []).slice(0, 3).map((it) => <li key={it.id}>{it.description} — {Number(it.quantity)} @ ₹{it.rate}{it.is_capital && <span className="ml-1 rounded bg-violet-500/10 px-1 text-[10px] text-violet-600 dark:text-violet-400">Asset</span>}</li>)}
                      {(po.items || []).length > 3 && <li>+ {po.items.length - 3} more</li>}
                    </ul>
                  </TableCell>
                  <TableCell className="min-w-[10rem]">
                    {canWrite && po.status !== "CANCELLED" ? (
                      <Select value={po.project_id || "none"} onValueChange={(v) => linkProject(po, v === "none" ? null : v)}>
                        <SelectTrigger className={`h-8 text-xs ${po.project_id ? "" : "border-amber-500/60 text-amber-600 dark:text-amber-400"}`} data-testid={`po-project-select-${po.number}`}>
                          <SelectValue placeholder="Not linked" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not linked</SelectItem>
                          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground">{projects.find((p) => p.id === po.project_id)?.code || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-foreground">{vendorName(po.vendor_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{po.po_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{po.total_amount}</TableCell>
                  <TableCell><StatusPill value={po.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {canWrite && po.status === "DRAFT" && <Button size="sm" variant="outline" disabled={busyId === po.id} onClick={() => act(po, "submit")} data-testid={`submit-po-button-${po.number}`}><Send className="h-3.5 w-3.5" /></Button>}
                      {po.status === "SUBMITTED" && (
                        <>
                          <Button size="sm" variant="outline" disabled={busyId === po.id} onClick={() => act(po, "approve")} data-testid={`approve-po-button-${po.number}`}><Check className="h-3.5 w-3.5 text-emerald-500" /></Button>
                          <Button size="sm" variant="outline" disabled={busyId === po.id} onClick={() => act(po, "reject")} data-testid={`reject-po-button-${po.number}`}><X className="h-3.5 w-3.5 text-rose-400" /></Button>
                        </>
                      )}
                      <ApprovalReviewButton docType="PO" docId={po.id} onChanged={load} />
                      {canWrite && <DeleteDocButton path={`/procurement/purchase-orders/${po.id}`} label="Purchase order" number={po.number} status={po.status} onDeleted={load} />}
                      <DocumentFlowButton docType="PURCHASE_ORDER" docId={po.id} cancelEndpoint={po.status === "APPROVED" ? `/procurement/purchase-orders/${po.id}/cancel` : undefined} onCancelled={load} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreatePODialog({ companies, vendors, rfqs, requisitions, projects, onCreated }) {
  const EMPTY_FORM = { company_id: "", project_id: "", vendor_id: "", po_date: "", rfq_id: "", requisition_id: "", tax_code_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [source, setSource] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [taxCodes, setTaxCodes] = useState([]);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.rate,
  } : it)));

  useEffect(() => {
    if (open) api.get("/masters/tax-codes").then(({ data }) => setTaxCodes((data || []).filter((t) => t.is_active !== false))).catch(() => {});
  }, [open]);

  // Documents a PO can be copied from: open RFQs, and approved requisitions not yet converted
  const openRfqs = (rfqs || []).filter((r) => !["CLOSED", "CONVERTED", "CANCELLED"].includes(r.status));
  const openRequisitions = (requisitions || []).filter((r) => r.status === "APPROVED");
  const vendorName = (id) => (vendors || []).find((v) => v.id === id)?.name;

  const rateFromQuote = (rfq, invite, item) => {
    if (invite?.line_rates && invite.line_rates[item.id] != null) return String(invite.line_rates[item.id]);
    if (invite?.quoted_amount != null && rfq.items.length === 1) return (Number(invite.quoted_amount) / Number(item.quantity)).toFixed(2);
    return item.estimated_rate != null ? String(item.estimated_rate) : "";
  };

  const pickSource = (value) => {
    setSource(value);
    if (!value) return;
    const [kind, id] = value.split(":");
    if (kind === "rfq") {
      const rfq = openRfqs.find((r) => r.id === id);
      const invite = rfq.invited_vendors.find((v) => v.is_selected) || null;
      setForm({ ...form, company_id: rfq.company_id, project_id: rfq.project_id || form.project_id, vendor_id: invite?.vendor_id || form.vendor_id, rfq_id: rfq.id, requisition_id: "" });
      setItems(rfq.items.map((it) => ({ description: it.description, uom_id: it.uom_id || null, quantity: String(Number(it.quantity)), rate: rateFromQuote(rfq, invite, it) })));
      if (!invite) toast.info("No vendor is selected on this RFQ yet — choose the vendor below");
    } else {
      const pr = openRequisitions.find((r) => r.id === id);
      setForm({ ...form, company_id: pr.company_id, project_id: pr.project_id || form.project_id, rfq_id: "", requisition_id: pr.id });
      setItems(pr.items.map((it) => ({ description: it.description, uom_id: it.uom_id || null, quantity: String(Number(it.quantity)), rate: it.estimated_rate != null ? String(it.estimated_rate) : "" })));
    }
  };

  const reset = () => { setForm(EMPTY_FORM); setSource(""); setItems([{ ...EMPTY_ITEM }]); };
  const total = items.reduce((sum, it) => sum + Number(it.quantity || 0) * Number(it.rate || 0), 0);
  const inr = (n) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/purchase-orders", { ...form, project_id: form.project_id || null, tax_code_id: form.tax_code_id || null, rfq_id: form.rfq_id || null, requisition_id: form.requisition_id || null, items: items.map((it) => ({ ...it, wbs_item_id: it.wbs_item_id || null })) });
      toast.success("Purchase order created");
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild><Button data-testid="create-po-button"><Plus className="h-4 w-4" /> New Purchase Order</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-po-form">
          <div className="rounded-lg border border-dashed border-border p-3">
            <Label>Copy from <span className="font-normal text-muted-foreground">(fills company, vendor, items and rates)</span></Label>
            <Select value={source} onValueChange={pickSource}>
              <SelectTrigger data-testid="po-source-select" className="mt-1.5"><SelectValue placeholder="Start blank, or pick an RFQ / requisition" /></SelectTrigger>
              <SelectContent>
                {openRfqs.map((r) => {
                  const sel = r.invited_vendors.find((v) => v.is_selected);
                  return (
                    <SelectItem key={r.id} value={`rfq:${r.id}`}>
                      RFQ {r.number}{sel ? ` · ${vendorName(sel.vendor_id) || "selected vendor"}${sel.quoted_amount != null ? ` · ₹${Number(sel.quoted_amount).toLocaleString("en-IN")}` : ""}` : " · no vendor selected"}
                    </SelectItem>
                  );
                })}
                {openRequisitions.map((r) => (
                  <SelectItem key={r.id} value={`pr:${r.id}`}>Requisition {r.number} · {r.items.length} item(s) · estimated rates</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {openRfqs.length === 0 && openRequisitions.length === 0 && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">No open RFQs or approved requisitions to copy from.</p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="po-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="po-date-input" value={form.po_date} onChange={set("po_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(the site this material is for — its cost is booked there)</span></Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="po-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {!form.project_id && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Without a project, this material's cost won't appear in any project's finance.</p>}
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              <div className="flex items-center gap-2 px-0.5 text-[11px] text-muted-foreground">
                <span className="flex-1">Description</span><span className="w-24">Qty</span><span className="w-24">Rate</span><span className="w-28 text-right">Amount</span><span className="w-14 text-center" title="Tick for machines / vehicles / equipment the company keeps (fixed assets) — not site material">Asset?</span>{items.length > 1 && <span className="w-9" />}
              </div>
              <p className="px-0.5 text-[11px] text-muted-foreground">Link each line to a WBS activity so its cost shows phase-wise in Cost to Completion.</p>
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`po-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`po-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`po-item-qty-input-${idx}`} />
                    <Input required type="number" step="0.01" placeholder="Rate" className="w-24" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} data-testid={`po-item-rate-input-${idx}`} />
                    <span className="w-28 text-right font-mono text-xs text-foreground">{inr(Number(it.quantity || 0) * Number(it.rate || 0))}</span>
                    <span className="flex w-14 justify-center"><input type="checkbox" title="Fixed asset (machine / vehicle / equipment)" checked={Boolean(it.is_capital)} onChange={(e) => updateItem(idx, "is_capital", e.target.checked)} data-testid={`po-item-capital-${idx}`} /></span>
                    {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-po-item-button-${idx}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`po-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-po-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>GST / tax code</Label>
              <Select value={form.tax_code_id || "none"} onValueChange={(v) => setForm({ ...form, tax_code_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="po-tax-select" className="mt-1.5"><SelectValue placeholder="No tax" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No tax</SelectItem>
                  {taxCodes.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name} ({Number(t.rate)}%)</SelectItem>)}
                </SelectContent>
              </Select>
              {taxCodes.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">Add tax codes in Masters → Tax Codes.</p>}
            </div>
            <div className="space-y-0.5 self-end text-right text-sm" data-testid="po-total">
              {(() => {
                const rate = Number(taxCodes.find((t) => t.id === form.tax_code_id)?.rate || 0);
                const tax = (total * rate) / 100;
                return (
                  <>
                    <div className="text-muted-foreground">Subtotal {inr(total)}</div>
                    <div className="text-muted-foreground">GST {rate}% {inr(tax)}</div>
                    <div className="font-semibold text-foreground">Total {inr(total + tax)}</div>
                  </>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-po-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
